/**
 * Master data for the voucher screens.
 *
 * There is no API yet (see `docs/roadmap.md` Phase 2), so this module stands in
 * for the endpoints the forms will call: `GET /accounts?postable=1`,
 * `GET /parties`, `GET /periods`, `GET /fx-rates`. It is
 * copied from `packages/db/seed/0002_company.sql` and `0003_parties.sql` rather
 * than invented, so every code, flag and account name on screen is one that
 * exists in a seeded database — which means the validation the forms perform is
 * the validation the real data will trigger.
 *
 * Each shape below carries only the columns the entry screens read. When the API
 * lands, the types stay and the constants become query results.
 */

export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
export type NormalBalance = 'DR' | 'CR';
export type ControlKind =
  | 'CASH'
  | 'BANK'
  | 'RECEIVABLE'
  | 'PAYABLE'
  | 'INVENTORY'
  | 'TAX'
  | 'COMMISSION_RECEIVABLE'
  | 'COMMISSION_PAYABLE'
  | 'FIXED_ASSET';

export interface Account {
  code: string;
  name: string;
  type: AccountType;
  normalBalance: NormalBalance;
  /** NULL for an ordinary account. Control accounts are reached through their
   *  sub-ledger document, which is what `allowDirectPosting` gates. */
  control: ControlKind | null;
  /** `accounts.allow_direct_posting`. False blocks a manual JV line only —
   *  `trg_voucher_lines_derive` restricts the check to `voucher_type = 'JV'`,
   *  because a receipt must be able to credit Accounts Receivable. */
  allowDirectPosting: boolean;
  /**
   * `accounts.fcy_currency`. An account naming a foreign currency refuses any
   * line that is neither that currency nor base. Null for a base-only account.
   */
  fcyCurrency: string | null;
  /** A sub-ledger posting without a party cannot be reconciled. */
  requiresParty: boolean;
  /** `trg_voucher_lines_derive` rejects a posting to a group head outright. */
  isLeaf: boolean;
  /** 1-based, as an accountant counts: `accounts.depth + 1`. */
  level: number;
  parentCode: string | null;
  /** Direct sub-accounts. More than zero is why `isLeaf` went false. */
  childCount: number;
  /**
   * The account's balance in base currency, signed — positive is a debit.
   *
   * Rolled up over the subtree by `GET /api/accounts`, so a head carries what
   * its children add up to. It is what the ledger held when the chart was last
   * fetched, which is the honest thing to say beside a line somebody is still
   * typing: the voucher on screen has not posted, so it is not in this figure.
   */
  balanceBase: string;
  /** `accounts.is_bank_account`. The rows the bank-account picker offers. */
  isBank: boolean;
}

/**
 * The level a sub-account's parent sits at.
 *
 * Levels 1 to 4 are the chart an accountant designs; level 5 is the detail
 * underneath — the individual customer, supplier or salesperson. The entry
 * screens read this to decide which accounts to offer directly and which to
 * offer as a parent with its sub-accounts behind it. `ChartOfAccounts.tsx`
 * holds the same constant for the maintenance side of the rule.
 */
export const SUB_ACCOUNT_PARENT_LEVEL = 4;

/**
 * The chart of accounts, as served by `GET /api/accounts`.
 *
 * A module-level cache rather than a prop threaded through `summarise()` and
 * `validate()`: those are pure functions over a draft, and the chart is reference
 * data that changes when somebody edits the chart of accounts — not per render.
 * `App` fills it once from the API before any entry screen mounts.
 */
let INDEX = new Map<string, Account>();
let LIST: Account[] = [];
let CHILDREN = new Map<string, Account[]>();

export function setAccounts(accounts: Account[]): void {
  LIST = accounts;
  INDEX = new Map(accounts.map((account) => [account.code, account]));

  // Built once here rather than filtered per render: the sub-account picker asks
  // for a parent's children on every keystroke in a voucher line.
  CHILDREN = new Map();
  for (const account of accounts) {
    if (!account.parentCode) continue;
    const siblings = CHILDREN.get(account.parentCode);
    if (siblings) siblings.push(account);
    else CHILDREN.set(account.parentCode, [account]);
  }
}

export function allAccounts(): Account[] {
  return LIST;
}

/** Only leaves are offered: a group head cannot be posted to. */
export function postableAccounts(): Account[] {
  return LIST.filter((account) => account.isLeaf);
}

/** The direct sub-accounts of `code`, in chart order. */
export function subAccountsOf(code: string): Account[] {
  return CHILDREN.get(code) ?? [];
}

/** Has this account been given 5th-level detail? Then it takes no lines itself. */
export function hasSubAccounts(code: string): boolean {
  return (CHILDREN.get(code)?.length ?? 0) > 0;
}

/**
 * What the Account column of a voucher offers.
 *
 * Two kinds of row, and they are not the same thing:
 *
 *   - **a leaf at level 4 or above** — postable as it stands, which is every
 *     account the picker used to offer;
 *   - **a 4th-level head that has sub-accounts** — not postable itself, but the
 *     way to reach the level-5 detail that is. Picking it opens the sub-account
 *     column, and the posting lands on whatever is chosen there.
 *
 * Level-5 accounts are deliberately absent. They are reachable only through
 * their parent, which is what stops a picker holding two thousand customers
 * from being the first thing an accountant has to scroll past to find `6420
 * Vehicle & Fuel`. Group heads at levels 1 to 3 are absent because they are
 * structure: nothing posts to them and nothing ever will.
 */
export function entryAccounts(): Account[] {
  return LIST.filter((account) =>
    account.isLeaf
      ? account.level <= SUB_ACCOUNT_PARENT_LEVEL
      : account.level === SUB_ACCOUNT_PARENT_LEVEL && account.childCount > 0,
  );
}

/**
 * Split a posted account code back into the pair the grid edits.
 *
 * The ledger stores one account per line — the level-5 sub-account, because that
 * is the leaf the posting had to target. The form shows two columns, so loading
 * a saved voucher has to put the parent in one and the child in the other.
 * Anything that is not level-5 detail is its own account and has no sub-account.
 */
export function splitPostingAccount(code: string): {
  accountCode: string;
  subAccountCode: string;
} {
  const account = INDEX.get(code);
  if (account?.parentCode && account.level > SUB_ACCOUNT_PARENT_LEVEL) {
    return { accountCode: account.parentCode, subAccountCode: code };
  }
  return { accountCode: code, subAccountCode: '' };
}

/**
 * The postable cash or bank accounts, for a contra picker.
 *
 * `control_kind` is the chart's own answer and is what the seed sets on every
 * till and every bank. `is_bank_account` is accepted as well because it is the
 * flag Setup → Bank Accounts writes, and a bank opened there but never marked
 * as a control account is still a bank a Bank Payment must be able to name.
 */
export function accountsOfKind(kind: 'CASH' | 'BANK'): Account[] {
  return LIST.filter(
    (account) =>
      account.isLeaf && (account.control === kind || (kind === 'BANK' && account.isBank)),
  );
}

/**
 * The account a cash or bank voucher opens on.
 *
 * The first postable account of that kind in **chart order**, which since 0037 is
 * `account_code_sort` order from the API — so it is the first till and the first
 * bank as an accountant reading the chart would name them.
 *
 * This replaced `defaultAccountCode: '1111'` and `'1121'` in
 * `voucher-config.ts`. Those were the seeded codes of the one till and the first
 * bank, and 0037 renumbered both — but the deeper problem is that a *literal*
 * there is wrong in any book whose chart was built rather than seeded, and it
 * fails invisibly: `validate()` reports "no account 1121 in this chart" against a
 * code nobody chose and no picker offered.
 *
 * Returns `''` while the registry is still empty, which is the state the caller
 * has to handle anyway — it is filled during `App`'s render, and a value derived
 * once before that is never recomputed.
 */
export function defaultAccountOfKind(kind: 'CASH' | 'BANK'): string {
  return accountsOfKind(kind)[0]?.code ?? '';
}

export function findAccount(code: string): Account | undefined {
  return INDEX.get(code);
}

/** `'1121 · Meezan Bank 0192 — Current PKR'` — how every picker labels a line. */
export function accountLabel(code: string): string {
  const account = INDEX.get(code);
  return account ? `${account.code} · ${account.name}` : code;
}

// ── Parties ──────────────────────────────────────────────────────────────────

export interface Party {
  code: string;
  name: string;
  kind: 'CUSTOMER' | 'SUPPLIER' | 'BROKER_SELLER' | 'SALESPERSON';
}

/*
 * `controlAccount` used to be a fourth field here, holding `'1131'`, `'2111'`,
 * `'2112'` or `'2120'` per party. It was read in one place — as Combobox search
 * keywords — and 0037 renumbered every one of those codes, so it had become
 * fourteen strings naming accounts the chart does not carry, matched by a search
 * nobody could type a hit for. Which control account a party sits under is the
 * chart's own answer (`accounts.party_id`) and this list is not where to keep a
 * second one.
 */

export const PARTIES: Party[] = [
  {
    code: 'CUS-0001',
    name: 'Sadiq Poultry Farm',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0002',
    name: 'Rahim Traders',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0003',
    name: 'Chenab Feeds Limited',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0004',
    name: 'Hilal Poultry Supply',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0005',
    name: 'Al-Noor Broilers',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0006',
    name: 'Frontier Feed Mills',
    kind: 'CUSTOMER',
  },
  { code: 'SUP-0001', name: 'Punjab Oil Mills', kind: 'SUPPLIER' },
  { code: 'SUP-0002', name: 'Indus Corn Products', kind: 'SUPPLIER' },
  { code: 'SUP-0003', name: 'Karachi Clearing Agency', kind: 'SUPPLIER' },
  { code: 'SUP-1001', name: 'Kwan Agro Ltd', kind: 'SUPPLIER' },
  { code: 'SUP-1002', name: 'Rosario Grain Export', kind: 'SUPPLIER' },
  { code: 'SUP-1003', name: 'Paranagua Soy Trading', kind: 'SUPPLIER' },
  { code: 'SUP-1004', name: 'Gulf Marine Proteins', kind: 'SUPPLIER' },
  { code: 'PTA-0001', name: 'Sadiq Feed Mills', kind: 'BROKER_SELLER' },
  { code: 'PTA-0002', name: 'Andes Protein SA', kind: 'BROKER_SELLER' },
  { code: 'PTA-0003', name: 'Delta Grain FZE', kind: 'BROKER_SELLER' },
  {
    code: 'SP-01',
    name: 'Kamran Sheikh (salesperson)',
    kind: 'SALESPERSON',
  },
  { code: 'SP-02', name: 'Bilal Ahmed (salesperson)', kind: 'SALESPERSON' },
];

const PARTY_INDEX = new Map(PARTIES.map((party) => [party.code, party]));

export function findParty(code: string): Party | undefined {
  return PARTY_INDEX.get(code);
}

export function partyLabel(code: string): string {
  const party = PARTY_INDEX.get(code);
  return party ? `${party.name} (${party.code})` : code;
}

// ── Branch ───────────────────────────────────────────────────────────────────
// The one dimension left on a voucher, and it is tenancy rather than analysis:
// chosen once in the top bar, not per document. Cost centre, zone and business
// line were dropped from the ledger in 0019_simplify_ledger.sql.

export const BRANCHES = [
  { code: 'HO', name: 'Gujranwala' },
  { code: 'LHR', name: 'Lahore Branch' },
];

// ── The company ──────────────────────────────────────────────────────────────

/**
 * The company profile, as `GET /api/context` serves it.
 *
 * One record, filled by `App`, read by the top bar, every report header and
 * every printed voucher. It is a registry for the same reason the chart of
 * accounts is: the printed voucher is generated by a function with no React
 * tree above it, and threading fifteen fields of letterhead through the voucher
 * screen to reach it would make them props of things that do not care.
 *
 * Everything on it is maintained in **Setup → Company Profile**, which is the
 * point: the name in the top bar, the letterhead on a trial balance and the
 * heading on a printed Cash Payment are the same six fields, and they used to
 * be a literal `'biovet'` in `App.tsx` and nothing at all on the reports.
 */
export interface CompanyRef {
  code: string;
  name: string;
  legalName: string;
  tagline: string | null;
  brandMark: string | null;
  logoUrl: string | null;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  /**
   * Which of the three identifiers a printed document carries (`0039`).
   *
   * Maintained under Setup → Company Profile. They are separate from whether the
   * company *holds* the number: unticking one withholds it from paper and from
   * an export, and leaves the value where it is. `printIdentifiers()` below is
   * the one place the pair is resolved.
   */
  printShowNtn: boolean;
  printShowStrn: boolean;
  printShowCuin: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  reportFooter: string | null;
  baseCurrency: string;
  timezone: string;
  /** Setup → Company Profile. Null where the book has no house unit. */
  defaultUomCode: string | null;
}

let COMPANY: CompanyRef | null = null;

export function setCompany(company: CompanyRef | null): void {
  COMPANY = company;
}

export function company(): CompanyRef | null {
  return COMPANY;
}

/**
 * The tax identifiers this company prints, already filtered.
 *
 * Two conditions per identifier — the company holds it, and the profile says to
 * print it — and this is the only place the pair is evaluated. Written out at
 * each of the four print composers, one of them would eventually check the value
 * and forget the flag, and the identifier the accountant had deliberately
 * withheld would reappear on one document out of four.
 */
export function printIdentifiers(): Array<{ label: string; value: string }> {
  const co = COMPANY;
  if (!co) return [];
  return [
    { label: 'NTN', value: co.printShowNtn ? (co.ntn ?? '') : '' },
    { label: 'STRN', value: co.printShowStrn ? (co.strn ?? '') : '' },
    { label: 'CUIN', value: co.printShowCuin ? (co.cuin ?? '') : '' },
  ].filter((i) => i.value !== '');
}

/** Where the books are kept, for the clock and for "today". */
export function companyTimezone(): string {
  return COMPANY?.timezone || 'Asia/Karachi';
}

/**
 * The company's address as one line, for a report heading.
 *
 * Blank parts are dropped rather than rendered as empty commas — a company that
 * has filled in a city and nothing else should print the city.
 */
export function companyLocation(): string {
  const co = COMPANY;
  if (!co) return '';
  return [co.addressLine1, co.addressLine2, co.city, co.province].filter(Boolean).join(', ');
}

// ── Units of measure ─────────────────────────────────────────────────────────

/**
 * The UoM register, cached the way currencies and the chart of accounts are.
 *
 * Three screens typed a quantity into a `<select>` holding the literals BAG, MT
 * and KG — so a unit added under Setup → Units of Measure was known to the
 * product, to every conversion factor and to the database, and could not be
 * chosen anywhere a document line is entered. `App` fills this from
 * `GET /api/context`.
 *
 * Empty until the API answers, which the callers below turn into "just what the
 * line already has" rather than an empty dropdown that erases a saved unit.
 */
export interface UomRef {
  code: string;
  name: string;
  dimension: string;
  decimals: number;
  /**
   * How many of the dimension's canonical unit one of this is — KG 1, MT 1000,
   * LTR 1, ML 0.001. Maintained under Setup → Units of Measure; the database
   * derives every product's conversion from it (`0036`), so this is the number
   * that decides whether a line posts, not a per-product field somebody fills in.
   */
  factorToBase?: string;
}

let UOMS: UomRef[] = [];

export function setUoms(uoms: UomRef[]): void {
  UOMS = uoms;
}

export function allUoms(): UomRef[] {
  return UOMS;
}

/**
 * The units a picker should offer, given the item on the line and what the line
 * already holds.
 *
 * **`baseUom` is what makes the picker agree with the posting rule.** A product
 * is stocked in one unit and can be transacted in any unit of the *same
 * dimension* — `uomFactor` derives the conversion from the register and refuses
 * anything else, because turning litres into kilograms needs a density this book
 * does not hold. The picker used to offer every unit in the register regardless,
 * so the only place the disagreement showed was at Post, in a refusal naming a
 * control that did not exist. Pass the picked product's base unit and the list
 * is exactly what will post.
 *
 * With no `baseUom` — a blank line with no item chosen yet — the whole register
 * is offered, because there is nothing yet to narrow it against.
 *
 * A line loaded with a unit that has since been deactivated or retired keeps it
 * as an option either way: the alternative is a dropdown that silently re-points
 * a historical document at a different unit the first time somebody opens it.
 */
export function uomOptions(current?: string, baseUom?: string | null): UomRef[] {
  const base = baseUom ? UOMS.find((u) => u.code === baseUom) : undefined;
  const offered = base ? UOMS.filter((u) => u.dimension === base.dimension) : UOMS;
  if (!current || offered.some((u) => u.code === current)) return offered;
  const held = UOMS.find((u) => u.code === current);
  return [...offered, held ?? { code: current, name: current, dimension: 'WEIGHT', decimals: 3 }];
}

/** The base unit of a product on a line, for `uomOptions`. */
export function baseUomOf(
  sku: string,
  products: Array<{ sku: string; baseUom?: string }> | undefined,
): string | undefined {
  if (!sku) return undefined;
  return (products ?? []).find((p) => p.sku === sku)?.baseUom;
}

/**
 * The unit a blank document line should open on.
 *
 * Three answers, in order of how much they know about the line:
 *
 * 1. **The product's own stock unit**, when an item has been chosen. Nothing a
 *    company-wide setting could say beats what the SKU itself is stocked in.
 * 2. **The company default** (Setup → Company Profile, `0032`), for a line with
 *    no item yet.
 * 3. **The first unit in the register**, so a book whose administrator has set
 *    no default still opens on something real rather than on a blank cell.
 *
 * This replaced three literals — `'MT'` on the import consignment, `'BAG'` on
 * the local purchase and on the sales invoice — which were a default nobody
 * could change, wrong in any book that trades in something else, and wrong on
 * every line of every document until somebody edited the source.
 *
 * Returns `''` only when the register itself is empty, which is the moment
 * before `/api/context` answers.
 */
export function defaultUom(productStockUom?: string | null): string {
  if (productStockUom && UOMS.some((u) => u.code === productStockUom)) return productStockUom;
  const house = COMPANY?.defaultUomCode;
  if (house && UOMS.some((u) => u.code === house)) return house;
  return UOMS[0]?.code ?? '';
}

// ── Fiscal calendar ──────────────────────────────────────────────────────────

export type PeriodStatus = 'OPEN' | 'CLOSED' | 'FUTURE';

export interface Period {
  periodNo: number;
  name: string;
  startDate: string;
  endDate: string;
  status: PeriodStatus;
  isAdjustment?: boolean;
}

export interface FiscalYearRef {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

/**
 * The open fiscal year and its posting calendar, from `GET /api/context`.
 *
 * These were two constants — FY 2026 and twelve hand-built months — and the
 * effect was a book that could not be run in any other year: a voucher dated
 * 2027 was refused by the form with "open the next fiscal year first" however
 * many years Setup had actually opened, and a period the database had closed
 * still looked open on screen. They are a registry now, filled by `App` the
 * same way the chart of accounts is, and empty until the API answers.
 *
 * The calendar is **annual**: one period per fiscal year, plus the year-end
 * adjustment period. Nothing here assumes twelve of anything — it reads what
 * the database has, which is what `resolve_period()` will resolve against.
 */
let FY: FiscalYearRef | null = null;
let PERIOD_LIST: Period[] = [];

export function setFiscalCalendar(year: FiscalYearRef | null, periods: Period[]): void {
  FY = year;
  PERIOD_LIST = periods;
}

/**
 * The open fiscal year, or a null-object spanning nothing.
 *
 * Returning a year with an empty range rather than `null` keeps every caller a
 * plain comparison — and an empty range refuses every date, which is the safe
 * direction while the context request is in flight.
 */
export function fiscalYear(): FiscalYearRef {
  return FY ?? { code: '—', name: 'No open fiscal year', startDate: '', endDate: '' };
}

/** True once the API has answered. Screens use it to tell "loading" from "closed". */
export function hasFiscalCalendar(): boolean {
  return FY !== null;
}

export function periods(): Period[] {
  return PERIOD_LIST;
}

/**
 * The database's `resolve_period(company, date)`: the period is derived from the
 * voucher date, never chosen. The year-end adjustment period shares the year's
 * last day and is excluded here, exactly as `resolve_period` excludes it.
 */
export function resolvePeriod(voucherDate: string): Period | undefined {
  return PERIOD_LIST.find(
    (p) => !p.isAdjustment && voucherDate >= p.startDate && voucherDate <= p.endDate,
  );
}

/**
 * What a new voucher is dated.
 *
 * Today, when an open period covers it. Otherwise the open period's last day —
 * a book being worked outside its open year should still open on a form that
 * can be posted, rather than on a date error nobody can fix from the screen.
 */
export function defaultVoucherDate(now = new Date()): string {
  const today = companyToday(now);
  if (resolvePeriod(today)?.status === 'OPEN') return today;
  // `||`, not `??`: the null-object year carries an empty string rather than
  // null, and a form built before `/api/context` answers must open on today
  // rather than on a blank date field reporting "voucher date is required".
  return PERIOD_LIST.find((p) => p.status === 'OPEN')?.endDate || fiscalYear().endDate || today;
}

/**
 * The calendar month containing `date`, as the two dates a dated report takes.
 *
 * Shared by the Trial Balance and the General Ledger, which both open on the
 * current month rather than on the whole book: a report with movement columns
 * and no window is showing a year, and neither screen said so.
 */
export function monthOf(date: string): { from: string; to: string } {
  const [y, m] = date.split('-').map(Number);
  const year = y ?? new Date().getUTCFullYear();
  const month = m ?? 1;
  // Day 0 of the next month is the last day of this one, and `Date.UTC` does the
  // December rollover without a special case.
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

/** The whole month `offset` months away from the one `from` starts in. */
export function shiftMonth(from: string, offset: number): { from: string; to: string } {
  const [y, m] = from.split('-').map(Number);
  const anchor = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + offset, 1));
  return monthOf(anchor.toISOString().slice(0, 10));
}

/**
 * The open fiscal year so far: its start date to today.
 *
 * What a Profit & Loss opens on. The *fiscal* year rather than the calendar one
 * because `companies.fy_start_month`/`fy_start_day` exist precisely so a book
 * can run April-to-March, and `date_trunc('year', ...)` would report such a book
 * on the wrong nine months. Read from the calendar registry `App` fills, so it
 * follows whatever Setup has opened.
 *
 * Falls back to the calendar year to date while the context request is in
 * flight — the registry is empty then, and an empty `from` would be read by the
 * API as "since the beginning of the book".
 */
export function fiscalYearToDate(): { from: string; to: string } {
  const to = companyToday();
  const fy = fiscalYear();
  const from = fy.startDate || `${to.slice(0, 4)}-01-01`;
  // A year opened for next year sorts after today; showing a window that ends
  // before it starts would ask the API for nothing at all.
  return { from: from > to ? to : from, to };
}

/** Today, in the company's own timezone — the date an accountant means. */
export function companyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: companyTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

// ── FX ───────────────────────────────────────────────────────────────────────

/**
 * `fx_rates` is unique per day. This is the rate the top bar opens with; the
 * administrator changes it there, and every entry screen reads the selection.
 * A voucher no longer carries a rate *type* — 0019 dropped the column, because
 * a rate is a rate and the audit log already records who changed it.
 */
export const SPOT_USD_PKR = '278.4000';

/**
 * The currency register, cached the way the chart of accounts is.
 *
 * Every screen with a currency dropdown used to write `<option>PKR</option>`
 * and `<option>USD</option>` by hand, so Setup → Currencies could add a third
 * one the database would happily post and no screen would offer. A cache rather
 * than a prop for the same reason `setAccounts` is one: the pickers that need it
 * are four and five components deep inside Deal Entry and the brokerage
 * registers, and threading a list through every panel to reach two `<Select>`s
 * is how those panels acquire props that mean nothing to them.
 *
 * `App` fills it from `GET /api/context` before any entry screen mounts. Empty
 * until then, which the callers below turn into "just the base currency" rather
 * than into an empty dropdown.
 */
export interface CurrencyRef {
  code: string;
  name: string;
  isBase: boolean;
  /** Latest SPOT into base. Null when `fx_rates` has none for this currency. */
  rate: string | null;
}

let CURRENCIES: CurrencyRef[] = [];
let BASE_CURRENCY = 'PKR';

export function setCurrencies(currencies: CurrencyRef[]): void {
  CURRENCIES = currencies;
  BASE_CURRENCY = currencies.find((c) => c.isBase)?.code ?? BASE_CURRENCY;
}

export function allCurrencies(): CurrencyRef[] {
  return CURRENCIES;
}

/** The book's reporting currency. Rate 1 by definition. */
export function baseCurrency(): string {
  return BASE_CURRENCY;
}

/**
 * The live rate for a currency, or `null` when there is none.
 *
 * Null rather than '1' on purpose. A currency added this morning has a register
 * row and no `fx_rates` row, and defaulting it to par would convert a foreign
 * amount into the base ledger one-for-one — an entry that looks posted and is
 * wrong by the whole exchange rate. The callers show an empty rate field
 * instead, which somebody has to fill before the voucher balances.
 */
export function rateFor(code: string): string | null {
  if (code === BASE_CURRENCY) return '1';
  return CURRENCIES.find((c) => c.code === code)?.rate ?? null;
}

// The instrument list lived here — cheque, pay order, TT and the rest. 0020
// dropped `instrument_type`, `instrument_no`, `instrument_date` and
// `clearing_date` from `voucher_lines`, so a bank voucher records its reference
// in the narration like any other. `bank_statement_lines` still carries an
// instrument for the *statement* side; when Phase 6 builds reconciliation, that
// is the table the vocabulary belongs to.
